import React, { type FC, useContext, useEffect, useState, useMemo, useCallback } from 'react';

import { ModalHeader, ModalBody } from '@atlaskit/modal-dialog';
import { SpotlightManager } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { expValEquals } from '@confluence/feature-experiments';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { SHARE_AND_RESTRICT_DIALOG_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { type GeneralAccessLevel } from '../commonTypes.tsx';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../controller/Controller.tsx';
import { useShareRecommendedUsersAnalytics } from '../analytics/useShareRecommendedUsersAnalytics.tsx';

import { Header, type HeaderProps } from './header/Header.tsx';
import { Body, type BodyProps } from './body/Body.tsx';
import { LoadingBody } from './body/LoadingBody.tsx';
import { Footer, type FooterProps } from './footer/Footer.tsx';
import { type UsdLoadingHeightContext, useUsdLoadingHeight } from './useUsdLoadingHeight.ts';
import { Placement as ShareAndPermissionsModalPlacement } from '@post-office/placements/share-and-permissions-modal';

export type ShareAndRestrictDialogProps = {
	header: HeaderProps | undefined;
	body: BodyProps | undefined;
	footer: FooterProps | undefined;
	onCloseRequest: () => void;
	onCloseComplete: () => void;
	isLoading?: boolean;
	usdLoadingHeightContext?: UsdLoadingHeightContext;
	openAccessRequestsDialog?: () => void;
	onInitialDialogOpen?: () => void;
	analyticsAttributes?: {
		contentId: string;
		contentType: string;
		contentSubType?: string;
		isLivePage: boolean;
		isDraft: boolean;
		pageMode: string;
		generalAccessLevel: GeneralAccessLevel;
		hasInheritedRestrictions: boolean;
		openFrom?: string;
	};
};

/**
 *  Window height threshold below which we make the whole dialog scroll, instead of just the body
 *  The 888 number was arrived at by adding up:
 *
 * 	- The header height with the user picker maximally full  -  392px
 *  - The footer height, when we have added some users to the user picker w/o expanding to add a note: 137px
 *  - Adding a 240px allowance for the body to have some room to scroll
 * 	- Overall modal height is computed as calc(100vh -119px), so an extra 119px
 *
 *  Thus the minimum window height needed for the modal body to scroll and still be a nice UX becomes 888px.
 *  Basically this is still an educated guess, but hopefully it allows for enough room for the users to not encounter jank.
 *  If we get further complaints that the body does not scroll, we should just raise the number.
 *
 *  ... Or we can become more intelligent about the actual header / footer heights in real time and make the breakpoint dynamic.
 *  Such an approach feels like overkill at this stage.
 *  */
export const MINIMUM_WINDOW_HEIGHT_FOR_BODY_SCROLL = 888;

export const shouldScrollEntireModal = (): boolean => {
	const windowHeight = window.innerHeight || (window.top?.innerHeight ?? 0);
	return windowHeight < MINIMUM_WINDOW_HEIGHT_FOR_BODY_SCROLL;
};

export const calculateDefaultWidth = () => Math.min(600, window.innerWidth - 80);

export const ShareAndRestrictDialog: FC<ShareAndRestrictDialogProps> = ({
	header,
	body,
	footer,
	onCloseRequest,
	onCloseComplete,
	isLoading,
	usdLoadingHeightContext,
	analyticsAttributes,
	openAccessRequestsDialog,
	onInitialDialogOpen,
}) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { Modal } = useChoreographerAssets();

	// Extracting the analytics attributes so that the useEffect does not fire with every render
	const {
		contentId,
		contentType,
		contentSubType,
		isLivePage,
		isDraft,
		pageMode,
		generalAccessLevel,
		openFrom,
	} = analyticsAttributes || {};

	/** Determine modal scrolling behavior */
	const defaultShouldScrollInViewport = useMemo(shouldScrollEntireModal, []);
	const [shouldScrollInViewport, setShouldScrollViewport] = useState<boolean>(
		defaultShouldScrollInViewport,
	);

	const defaultDialogWidth = useMemo(calculateDefaultWidth, []);
	const [width, setWidth] = useState(defaultDialogWidth);

	const { addRecommendedUserCount, sendRecommendedUsersCount } =
		useShareRecommendedUsersAnalytics();

	const onSuggestedUserClick = useCallback(() => {
		addRecommendedUserCount();
	}, [addRecommendedUserCount]);

	useEffect(() => {
		const onResize = () => {
			setShouldScrollViewport(shouldScrollEntireModal);
			setWidth(calculateDefaultWidth);
		};

		onResize();
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, []);

	useEffect(() => {
		return () => sendRecommendedUsersCount();
	}, [sendRecommendedUsersCount]);

	useEffect(() => {
		if (!isLoading) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: `shareAndRestrictDialogModal`,
					attributes: {
						contentId,
						contentType,
						contentSubType,
						isLivePage,
						isDraft,
						pageMode,
						generalAccessLevel,
						openFrom: openFrom ?? 'ShareAndRestrictButton',
						source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					},
				},
			}).fire();
			experienceTracker.succeed({
				name: SHARE_AND_RESTRICT_DIALOG_LOAD_EXPERIENCE,
			});
		}
	}, [
		experienceTracker,
		isLoading,
		createAnalyticsEvent,
		contentId,
		contentType,
		contentSubType,
		isLivePage,
		isDraft,
		pageMode,
		openFrom,
		generalAccessLevel,
	]);

	const heightObserver = useUsdLoadingHeight();
	const isMainShareDialogLoaded = !isLoading && header?.titleAndButtons.title === 'share';
	const height = usdLoadingHeightContext
		? heightObserver.getUsdLoadingHeight(usdLoadingHeightContext)
		: undefined;

	const onCloseCompleteHandler = () => {
		heightObserver.stopObservingDialogHeight();
		onCloseComplete();
	};

	useEffect(() => {
		heightObserver.setCanRecordCurrentHeight(isMainShareDialogLoaded);
	}, [heightObserver, isMainShareDialogLoaded]);

	/**
	 * Initial dialog opening effect.
	 * Allows for programmatic opening of dialog variants
	 * by specifying the initialDialog property of the Controller.
	 * So far we only have the need to programmatically open Access Requests dialog
	 * But if we need to do so for Public Links or Share to Slack
	 * We can do so by extending this code
	 */
	useEffect(() => {
		/**
		 * This opening method only exists if initialDialog is defined
		 * Note the cleanup function onInitialDialogOpen
		 * The cleanup is needed to avoid getting stuck on whichever
		 * initialDialog was programmatically invoked
		 */
		if (openAccessRequestsDialog) {
			openAccessRequestsDialog();
			return () => onInitialDialogOpen?.();
		}
	}, [openAccessRequestsDialog, onInitialDialogOpen]);

	return (
		<SpotlightManager>
			<Modal
				height={height}
				width={width}
				onClose={onCloseRequest}
				onCloseComplete={onCloseCompleteHandler}
				onOpenComplete={heightObserver.startObservingDialogHeight}
				shouldScrollInViewport={shouldScrollInViewport}
				testId="modal-dialog-share-and-restrict"
			>
				{expValEquals('share_and_permissions_dialog_placement_enabled', 'cohort', 'variation') && (
					<ShareAndPermissionsModalPlacement />
				)}
				<ModalHeader hasCloseButton={false}>
					{header && <Header {...header} onSuggestedUserClick={onSuggestedUserClick} />}
				</ModalHeader>
				{isLoading ? (
					<LoadingBody />
				) : (
					<>
						{body && (
							<ModalBody>
								<Body {...body} />
							</ModalBody>
						)}
					</>
				)}
				{footer && <Footer {...footer} />}
			</Modal>
		</SpotlightManager>
	);
};
